import React, { useEffect, useRef, useState } from "react";
import { BEFORE_AFTER } from "../../landingContent";
import SectionHeading from "../SectionHeading";
import MediaPlaceholder from "../MediaPlaceholder";
import DeferredLandingVideo from "../DeferredLandingVideo";

const BeforeAfterSection = () => {
  const cardGridRef = useRef(null);
  const beforeCardRef = useRef(null);
  const afterCardRef = useRef(null);
  const [activeMobileCard, setActiveMobileCard] = useState("before");

  useEffect(() => {
    const cardGrid = cardGridRef.current;

    if (!cardGrid || typeof window === "undefined") {
      return undefined;
    }

    let animationFrameId = null;

    const updateActiveMobileCard = () => {
      animationFrameId = null;

      const gridRect = cardGrid.getBoundingClientRect();
      const gridCenter = gridRect.left + (gridRect.width / 2);
      const beforeCard = beforeCardRef.current;
      const afterCard = afterCardRef.current;

      if (!beforeCard || !afterCard) {
        return;
      }

      const getDistanceFromCenter = (card) => {
        const cardRect = card.getBoundingClientRect();
        return Math.abs((cardRect.left + (cardRect.width / 2)) - gridCenter);
      };

      const nextActiveCard = getDistanceFromCenter(afterCard) < getDistanceFromCenter(beforeCard)
        ? "after"
        : "before";

      setActiveMobileCard((currentActiveCard) => (
        currentActiveCard === nextActiveCard ? currentActiveCard : nextActiveCard
      ));
    };

    const requestActiveMobileCardUpdate = () => {
      if (animationFrameId === null) {
        animationFrameId = window.requestAnimationFrame(updateActiveMobileCard);
      }
    };

    updateActiveMobileCard();
    cardGrid.addEventListener("scroll", requestActiveMobileCardUpdate, { passive: true });
    window.addEventListener("resize", requestActiveMobileCardUpdate, { passive: true });

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      cardGrid.removeEventListener("scroll", requestActiveMobileCardUpdate);
      window.removeEventListener("resize", requestActiveMobileCardUpdate);
    };
  }, []);

  const scrollToMobileCard = (cardType) => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targetCard = cardType === "after" ? afterCardRef.current : beforeCardRef.current;

    setActiveMobileCard(cardType);
    targetCard?.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  return (
    <section className="landing-v2__section landing-v2-before-after" aria-labelledby="before-after-heading">
      <div className="landing-v2__container">
        <SectionHeading headline={BEFORE_AFTER.headline} headingLevel={2} />
        <p className="landing-v2__subheading landing-v2__subheading--center">
          {BEFORE_AFTER.supportingCopy}
        </p>

        <div className="landing-v2-before-after__mobile-toggle" role="group" aria-label="Choose before or after comparison">
          <button
            type="button"
            className={`landing-v2-before-after__mobile-toggle-btn${activeMobileCard === "before" ? " landing-v2-before-after__mobile-toggle-btn--active" : ""}`}
            onClick={() => scrollToMobileCard("before")}
            aria-pressed={activeMobileCard === "before"}
          >
            Before
          </button>
          <button
            type="button"
            className={`landing-v2-before-after__mobile-toggle-btn${activeMobileCard === "after" ? " landing-v2-before-after__mobile-toggle-btn--active" : ""}`}
            onClick={() => scrollToMobileCard("after")}
            aria-pressed={activeMobileCard === "after"}
          >
            After
          </button>
        </div>

        <div className="landing-v2-before-after__mobile-summary" aria-label="Compact before and after comparison">
          <article className="landing-v2-before-after__mobile-card landing-v2-before-after__mobile-card--before">
            <span className="landing-v2-before-after__card-label landing-v2-before-after__card-label--before">
              Before
            </span>
            <p>Tabs, timers, notes, alarms, slides</p>
          </article>
          <article className="landing-v2-before-after__mobile-card landing-v2-before-after__mobile-card--after">
            <span className="landing-v2-before-after__card-label landing-v2-before-after__card-label--after">
              After
            </span>
            <p>One screen, one tab, everything in sync</p>
          </article>
        </div>

        <div ref={cardGridRef} className="landing-v2__card-grid landing-v2__card-grid--2 landing-v2-before-after__card-grid">
          <article ref={beforeCardRef} className="landing-v2__card landing-v2-before-after__card--before">
            <span className="landing-v2-before-after__card-label landing-v2-before-after__card-label--before">
              {BEFORE_AFTER.before.title}
            </span>
            <MediaPlaceholder
              src={BEFORE_AFTER.before.imageSrc}
              alt={BEFORE_AFTER.before.imageAlt}
              label={BEFORE_AFTER.before.imageLabel}
              aspectRatio="16x9"
            />
            <ul className="landing-v2-before-after__list">
              {BEFORE_AFTER.before.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article ref={afterCardRef} className="landing-v2__card landing-v2-before-after__card--after">
            <span className="landing-v2-before-after__card-label landing-v2-before-after__card-label--after">
              {BEFORE_AFTER.after.title}
            </span>
            <DeferredLandingVideo
              src={BEFORE_AFTER.after.videoSrc}
              poster={BEFORE_AFTER.after.videoPoster}
              alt={BEFORE_AFTER.after.videoAlt}
              aspectRatio="16x9"
            />
            <ul className="landing-v2-before-after__list landing-v2-before-after__list--after">
              {BEFORE_AFTER.after.items.map((item) => (
                <li key={item}>
                  <span className="landing-v2-before-after__check" aria-hidden="true">
                    <i className="fa fa-check" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;
